import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Copy, CheckCircle, AlertTriangle, Clock, User } from "lucide-react";
import { toast } from "sonner";

export default function ViewCredentials() {
  const { accessToken } = useParams();
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchCredentials();
  }, [accessToken]);

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${API}/public/credentials/${accessToken}`);
      setCredentials(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Failed to load credentials";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (credentials?.credentials) {
      navigator.clipboard.writeText(credentials.credentials);
      setCopied(true);
      toast.success("Credentials copied to clipboard");
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7a1d42] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading secure credentials...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-lg">
          <CardHeader className="text-center border-b border-slate-100 bg-red-50">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-red-700">Access Denied</CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center">
            <p className="text-slate-600 mb-4">{error}</p>
            <p className="text-sm text-slate-500">
              If you believe this is an error, please contact IT support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header with logo */}
        <div className="text-center mb-6">
          <img 
            src="https://www.niello.com/wp-content/themes/theme/library/images/logo.png" 
            alt="Think Niello"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-[#7a1d42]">Secure Credentials</h1>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-[#7a1d42] to-[#5a1632]">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="text-white">
                <CardTitle className="text-white">Your Credentials</CardTitle>
                <p className="text-white/80 text-sm">Ticket: {credentials?.ticket_number}</p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Warning banner for one-time credentials */}
            {credentials?.is_one_time && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">One-Time Access</p>
                  <p className="text-sm text-amber-700">
                    {credentials.message || "This link will no longer be accessible after you leave this page. Please save these credentials now."}
                  </p>
                </div>
              </div>
            )}

            {/* Credentials display */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-start mb-3">
                <span className="text-sm font-medium text-slate-600">Credentials</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className={`gap-2 ${copied ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
                  data-testid="copy-credentials-button"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy All
                    </>
                  )}
                </Button>
              </div>
              <pre className="whitespace-pre-wrap font-mono text-sm text-slate-800 bg-white p-4 rounded border border-slate-200 max-h-96 overflow-auto" data-testid="credentials-content">
                {credentials?.credentials}
              </pre>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <User className="w-4 h-4" />
                <span>Sent by: <strong>{credentials?.sent_by}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span>{new Date(credentials?.sent_at).toLocaleString()}</span>
              </div>
            </div>

            {/* Security notice */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                For security, please change any passwords after first use and store credentials securely.
                <br />
                If you did not request these credentials, contact IT support immediately.
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-4">
          Niello IT Helpdesk • Secure Credential Delivery
        </p>
      </div>
    </div>
  );
}
